import type { NostrEvent } from "@setu/protocol";
import type { PublishResult } from "../contracts";

/**
 * In-flight publishes, awaiting their `OK`.
 *
 * Split out of the pool for the same reason {@link ./countRequests.CountRequests}
 * is: this shares nothing with the EVENT/EOSE path. It is keyed by (relay, event
 * id) rather than by subscription id, it has no CLOSE, and its lifecycle ends on
 * the first reply. The pool routes one frame type here and otherwise owns sockets.
 *
 * Every entry is bounded three ways, and all three matter to a caller waiting to
 * be told their note landed: an `OK`, a socket that dropped before one arrived, and
 * a deadline for a relay that accepts an EVENT and answers nothing at all. A
 * publish promise that never settles is a compose box that spins forever.
 */

interface Pending {
  readonly resolve: (result: PublishResult) => void;
  timer: ReturnType<typeof setTimeout> | undefined;
}

/** Sends a frame to one relay. Injected so this stays free of socket details. */
type SendFrame = (relay: string, frame: readonly unknown[]) => void;

/** Pending publishes for every relay, and the `OK` accounting behind them. */
export class PublishRequests {
  /** relay -> event id -> pending publish. */
  private readonly pending = new Map<string, Map<string, Pending>>();

  constructor(private readonly send: SendFrame) {}

  /** Sends `event` to `relay` and resolves on `OK`, drop, or the deadline. */
  to(
    event: NostrEvent,
    relay: string,
    timeoutMs: number,
  ): Promise<PublishResult> {
    return new Promise<PublishResult>((resolve) => {
      let settled = false;
      const settle = (result: PublishResult): void => {
        if (settled) return;
        settled = true;
        resolve(result);
      };
      const byEvent = this.pending.get(relay) ?? new Map<string, Pending>();
      const entry: Pending = { resolve: settle, timer: undefined };
      entry.timer = setTimeout(() => {
        this.forget(relay, event.id);
        settle({ relay, ok: false, message: "timed out waiting for OK" });
      }, timeoutMs);
      byEvent.set(event.id, entry);
      this.pending.set(relay, byEvent);
      this.send(relay, ["EVENT", event]);
    });
  }

  /** `["OK", <event id>, <ok>, <message>]` from a relay. */
  handleOk(
    relay: string,
    eventId: unknown,
    ok: unknown,
    message: unknown,
  ): void {
    if (typeof eventId !== "string") return;
    const entry = this.pending.get(relay)?.get(eventId);
    if (entry === undefined) return;
    this.forget(relay, eventId);
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    const result: { relay: string; ok: boolean; message?: string } = {
      relay,
      ok: ok === true,
    };
    // The relay's reason is preserved verbatim — it is the only thing that tells
    // a user why their note did not land.
    if (typeof message === "string" && message !== "") result.message = message;
    entry.resolve(result);
  }

  /** Fail everything waiting on a relay that went away. */
  failAll(relay: string, message: string): void {
    const byEvent = this.pending.get(relay);
    if (byEvent === undefined) return;
    this.pending.delete(relay);
    for (const entry of byEvent.values()) {
      if (entry.timer !== undefined) clearTimeout(entry.timer);
      entry.resolve({ relay, ok: false, message });
    }
  }

  /** Fail everything, everywhere. For pool shutdown. */
  failEverything(message: string): void {
    for (const relay of [...this.pending.keys()]) this.failAll(relay, message);
  }

  private forget(relay: string, eventId: string): void {
    const byEvent = this.pending.get(relay);
    if (byEvent === undefined) return;
    byEvent.delete(eventId);
    if (byEvent.size === 0) this.pending.delete(relay);
  }
}
